import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, FileText, Megaphone, ListOrdered, Workflow,
  Users, RefreshCw,
} from 'lucide-react';

const STAGE_COLOR = {
  Prospect: 'bg-slate-100 text-slate-700',
  Aware: 'bg-blue-100 text-blue-700',
  Engaged: 'bg-amber-100 text-amber-700',
  Qualified: 'bg-emerald-100 text-emerald-700',
  Won: 'bg-purple-100 text-purple-700',
  Lost: 'bg-rose-100 text-rose-700',
};

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 text-slate-400 text-[11px] uppercase tracking-wide">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <p className="text-2xl font-bold text-slate-900 mt-2">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [daily, setDaily] = useState([]);
  const [topTemplates, setTopTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [o, d, t] = await Promise.all([
        api.get('/api/analytics/overview'),
        api.get('/api/analytics/daily-sends?days=14'),
        api.get('/api/analytics/top-templates?limit=5'),
      ]);
      setData(o);
      setDaily(d.rows || []);
      setTopTemplates(t.top_templates || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading || !data) return <div className="p-12 text-sm text-slate-400">Loading analytics…</div>;

  const totalStages = Object.values(data.crm_stages).reduce((a, b) => a + b, 0);
  const maxDaily = Math.max(1, ...daily.map((r) => r.sent + r.failed));

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Analytics</h2>
          <p className="text-sm text-slate-500 mt-1">
            KPIs and trends across templates, campaigns, sequences, flows, and CRM.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2" data-testid="analytics-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        <Kpi icon={Megaphone} label="Sent (30d)" value={data.campaigns.sent_30d} sub={`${data.campaigns.failed_30d} failed`} />
        <Kpi icon={Users} label="Audience reached" value={data.campaigns.audience_30d} sub={`${data.campaigns.completed_30d} campaigns`} />
        <Kpi icon={ListOrdered} label="Sequences active" value={data.sequences.active_runs} sub={`${data.sequences.completed_runs_30d} completed (30d)`} />
        <Kpi icon={Workflow} label="Flow runs (7d)" value={data.flow_executions.last_7_days} sub={`${data.flow_executions.all_time} all-time`} />
        <Kpi icon={FileText} label="Templates" value={`${data.templates.approved}/${data.templates.total}`} sub="approved / total" />
        <Kpi icon={TrendingUp} label="Running campaigns" value={data.campaigns.running} />
        <Kpi icon={BarChart3} label="CRM contacts" value={totalStages} sub="across all stages" />
      </div>

      {/* Daily sends */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="daily-sends-chart">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Daily sends — last 14 days</h3>
        {daily.length === 0 ? (
          <p className="text-xs text-slate-400 py-8 text-center">No campaign sends in the last 14 days.</p>
        ) : (
          <div className="flex items-end gap-1 h-32">
            {daily.map((r) => {
              const total = r.sent + r.failed;
              const h = Math.round((total / maxDaily) * 100);
              return (
                <div key={r.date} className="flex-1 flex flex-col items-center gap-1" title={`${r.date}: ${r.sent} sent, ${r.failed} failed`}>
                  <div className="w-full flex flex-col-reverse" style={{ height: '100%' }}>
                    <div className="bg-emerald-400" style={{ height: `${(r.sent / maxDaily) * 100}%` }}></div>
                    <div className="bg-rose-400" style={{ height: `${(r.failed / maxDaily) * 100}%` }}></div>
                  </div>
                  <span className="text-[9px] text-slate-400">{r.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="crm-stages-card">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">CRM stage distribution</h3>
          <div className="space-y-2">
            {Object.entries(data.crm_stages).map(([stage, count]) => {
              const pct = totalStages > 0 ? Math.round((count / totalStages) * 100) : 0;
              return (
                <div key={stage} className="flex items-center gap-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${STAGE_COLOR[stage] || 'bg-slate-100 text-slate-700'} w-20 text-center`}>
                    {stage}
                  </span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div className="bg-[#00D1B2] h-full" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="text-xs font-mono text-slate-700 w-12 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="top-templates-card">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Top templates by sends</h3>
          {topTemplates.length === 0 ? (
            <p className="text-xs text-slate-400 py-4">No campaign data yet.</p>
          ) : (
            <div className="space-y-2">
              {topTemplates.map((t) => (
                <div key={t.template_name} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-slate-700">{t.template_name}</span>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-emerald-600">{t.sent} sent</span>
                    {t.failed > 0 && <span className="text-rose-600">{t.failed} failed</span>}
                    <span className="text-slate-400">{t.campaigns} campaigns</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
